'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

/**
 * Calculates and builds a new job object with updated proposed model values based off the passed in params.
 *
 * @param params the input params
 * @param params.job the original job
 * @param params.proposedRangeSetting the new proposed range setting (disregarded if null)
 * @param params.targetRangeSpread the new proposed target range spread (disregarded if null)
 * @param params.rangeType the new proposed range type (disregarded if null)
 * @param params.midpointAdjustment the new proposed midpointAdjustment (disregarded if null)
 * @param params.proposedMidpoint the new proposed midpoint value (disregarded if null)
 * @param params.payType the payType (must be 'annual' or 'hourly') defaults to annual
 * @param params.roundTo the round to nearest value (e.g. 1000, 1, .01, .0001)
 *                        NOTE: This will not be used if the RangeType='manual'
 * @returns new job object with updated proposed calculations
 */
var getRecalculatedJobData = exports.getRecalculatedJobData = function getRecalculatedJobData(_ref) {
  var job = _ref.job,
      proposedRangeSetting = _ref.proposedRangeSetting,
      targetRangeSpread = _ref.targetRangeSpread,
      rangeType = _ref.rangeType,
      midpointAdjustment = _ref.midpointAdjustment,
      proposedMidpoint = _ref.proposedMidpoint,
      proposedMinimum = _ref.proposedMinimum,
      proposedMaximum = _ref.proposedMaximum,
      _ref$payType = _ref.payType,
      payType = _ref$payType === undefined ? 'annual' : _ref$payType,
      roundTo = _ref.roundTo,
      buildMinMaxBy = _ref.buildMinMaxBy,
      minPercentageOfMid = _ref.minPercentageOfMid,
      maxPercentageOfMid = _ref.maxPercentageOfMid,
      maxPercentageMarketElement = _ref.maxPercentageMarketElement,
      minPercentageMarketElement = _ref.minPercentageMarketElement;


  if (rangeType === 'midpoint') {
    if (job.salary_midpoint) {
      proposedMidpoint = job.salary_midpoint * (1 + midpointAdjustment);
    }
  } else if (rangeType !== 'manual') {
    var value = job[proposedRangeSetting];
    switch (proposedRangeSetting) {
      case 'target_base_salary':
        proposedMidpoint = findMatchingJobFieldValue(job, 'market_base_salary', value);
        break;
      case 'target_total_cash':
        proposedMidpoint = findMatchingJobFieldValue(job, 'market_total_cash_paid', value);
        break;
      case 'target_total_target_cash':
        proposedMidpoint = findMatchingJobFieldValue(job, 'market_ttc', value);
        break;
      case 'target_total_direct':
        proposedMidpoint = findMatchingJobFieldValue(job, 'market_total_direct', value);
        break;
      case 'target_total_guaranteed_pay':
        proposedMidpoint = findMatchingJobFieldValue(job, 'total_guaranteed_pay', value);
        break;
      default:
        proposedMidpoint = value;
        break;
    }
  }

  if (!proposedMidpoint) {
    var _newJob = _extends({}, job, {
      proposed_minimum: null,
      proposed_midpoint: null,
      proposed_maximum: null
    });
    _newJob.status = getRecalculatedJobStatus(_newJob);
    return _newJob;
  }

  //  MPE-2284 Proposed Min/Mid/Max Should be Rounded at computation time
  if (payType === 'hourly') {
    //  convert the annual midpoint to hourly then round
    proposedMidpoint = convertAnnualToHourlyValue(proposedMidpoint);

    //  1) if rounding is specified, then do the rounding at the hourly value and then convert that to annual value
    //  2) if rounding is NOT specified, then do not rounding at all since that is the closest representation
    //      to the hourly value since data is stored in annual type
    if (roundTo) {
      proposedMidpoint = Math.round(proposedMidpoint / roundTo) * roundTo;
    }

    if (buildMinMaxBy === 'manual') {
      proposedMinimum = convertAnnualToHourlyValue(proposedMinimum);
      proposedMaximum = convertAnnualToHourlyValue(proposedMaximum);

      if (roundTo) {
        proposedMinimum = Math.round(proposedMinimum / roundTo) * roundTo;
        proposedMaximum = Math.round(proposedMaximum / roundTo) * roundTo;
      }
    } else {
      proposedMinimum = calculateMinimumSalary(targetRangeSpread, proposedMidpoint, roundTo, buildMinMaxBy, minPercentageOfMid, minPercentageMarketElement, rangeType, job, payType);
      proposedMaximum = calculateMaximumSalary(proposedMidpoint, proposedMinimum, roundTo, buildMinMaxBy, maxPercentageOfMid, maxPercentageMarketElement, rangeType, job, payType);
    }
    //  convert all hourly values back to annual
    proposedMidpoint = convertHourlyValueToAnnual(proposedMidpoint);
    proposedMinimum = convertHourlyValueToAnnual(proposedMinimum);
    proposedMaximum = convertHourlyValueToAnnual(proposedMaximum);
  } else {
    //  default to annual pay with a default rounding to closest penny if rounding isn't specified
    if (!roundTo) {
      roundTo = 0.01;
    }

    proposedMidpoint = Math.round(proposedMidpoint / roundTo) * roundTo;

    if (buildMinMaxBy === 'manual') {
      proposedMinimum = Math.round(proposedMinimum / roundTo) * roundTo;
      proposedMaximum = Math.round(proposedMaximum / roundTo) * roundTo;
    } else {
      proposedMinimum = calculateMinimumSalary(targetRangeSpread, proposedMidpoint, roundTo, buildMinMaxBy, minPercentageOfMid, minPercentageMarketElement, rangeType, job, payType);
      proposedMaximum = calculateMaximumSalary(proposedMidpoint, proposedMinimum, roundTo, buildMinMaxBy, maxPercentageOfMid, maxPercentageMarketElement, rangeType, job, payType);
    }
  }

  var newJob = _extends({}, job, {
    proposed_minimum: proposedMinimum,
    proposed_midpoint: proposedMidpoint,
    proposed_maximum: proposedMaximum
  });
  newJob.status = getRecalculatedJobStatus(newJob);
  return newJob;
};

var findMatchingJobFieldValue = exports.findMatchingJobFieldValue = function findMatchingJobFieldValue(job, fieldPrefix, fieldPostfix) {
  var field = fieldPrefix + '_' + fieldPostfix;
  if (field in job) {
    return job[field];
  } else {
    return null;
  }
};

var convertAnnualToHourlyValue = exports.convertAnnualToHourlyValue = function convertAnnualToHourlyValue(value) {
  if (!value) return value;
  return value / 2080;
};

var convertHourlyValueToAnnual = exports.convertHourlyValueToAnnual = function convertHourlyValueToAnnual(value) {
  if (!value) return value;
  return value * 2080;
};

/**
 * Determines and returns the appropriate status string for a job based off proposed and current salary data.
 * @param job the job
 * @returns {string} the status string (current or proposed)
 */
var getRecalculatedJobStatus = exports.getRecalculatedJobStatus = function getRecalculatedJobStatus(job) {
  var status = 'Proposed';
  var currentHasRange = job.salary_minimum && job.salary_midpoint && job.salary_maximum;
  var proposedHasRange = job.proposed_minimum && job.proposed_midpoint && job.proposed_maximum;
  var areEqual = job.salary_minimum === job.proposed_minimum && job.salary_midpoint === job.proposed_midpoint && job.salary_maximum === job.proposed_maximum;
  if (currentHasRange && proposedHasRange && areEqual) status = 'Current';
  return status;
};

/**
 * Calculates the annual minimum salary based off range spread and annual midpoint salary
 * @param rangeSpread range spread
 * @param midpoint midpoint salary
 * @param roundTo round to the nearest value (e.g. 1, .01, .001, 1000)
 * @returns {number} minimum salary (annual)
 */
var calculateMinimumSalary = exports.calculateMinimumSalary = function calculateMinimumSalary(rangeSpread, midpoint) {
  var roundTo = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var buildMinMaxBy = arguments[3];
  var minPercentageOfMid = arguments[4];
  var minPercentageMarketElement = arguments[5];
  var rangeType = arguments[6];
  var job = arguments[7];
  var payType = arguments[8];

  var minValue = void 0;
  if (buildMinMaxBy === 'target') {
    minValue = 2 * midpoint / (2 + rangeSpread);
    if (roundTo !== null) {
      return Math.round(minValue / roundTo) * roundTo;
    } else {
      return minValue;
    }
  } else if (buildMinMaxBy === 'percent') {
    var value = job[minPercentageMarketElement];
    var proposedMinimum = void 0;
    switch (minPercentageMarketElement) {
      case 'target_base_salary':
        proposedMinimum = findMatchingJobFieldValue(job, 'market_base_salary', value);
        break;
      case 'target_total_cash':
        proposedMinimum = findMatchingJobFieldValue(job, 'market_total_cash_paid', value);
        break;
      case 'target_total_target_cash':
        proposedMinimum = findMatchingJobFieldValue(job, 'market_ttc', value);
        break;
      case 'target_total_direct':
        proposedMinimum = findMatchingJobFieldValue(job, 'market_total_direct', value);
        break;
      case 'target_total_guaranteed_pay':
        proposedMinimum = findMatchingJobFieldValue(job, 'total_guaranteed_pay', value);
        break;
      default:
        proposedMinimum = value;
        break;
    }
    if (payType === 'hourly') proposedMinimum = convertAnnualToHourlyValue(proposedMinimum);
    minValue = proposedMinimum * minPercentageOfMid;
    if (roundTo !== null) {
      if (rangeType === "market") {
        return Math.round(minValue / roundTo) * roundTo;
      } else {
        return Math.round(midpoint * minPercentageOfMid / roundTo) * roundTo;
      }
    } else {
      if (rangeType === "market") {
        return minValue;
      } else {
        return midpoint * minPercentageOfMid;
      }
    }
  }
};

/**
 * Calculates the annual maximum salary based off annual minimum salary and annual midpoint salary
 * @param midpoint midpoint salary
 * @param minimum minimum salary
 * @param roundTo round to the nearest value (e.g. 1, .01, .001, 1000)
 * @returns {number} maximum salary (annual)
 */
var calculateMaximumSalary = exports.calculateMaximumSalary = function calculateMaximumSalary(midpoint, minimum) {
  var roundTo = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var buildMinMaxBy = arguments[3];
  var maxPercentageOfMid = arguments[4];
  var maxPercentageMarketElement = arguments[5];
  var rangeType = arguments[6];
  var job = arguments[7];
  var payType = arguments[8];

  var maxValue = void 0;
  if (buildMinMaxBy === 'target') {
    maxValue = midpoint * 2 - minimum;
    if (roundTo !== null) {
      return Math.round(maxValue / roundTo) * roundTo;
    } else {
      return maxValue;
    }
  } else if (buildMinMaxBy === 'percent') {
    var value = job[maxPercentageMarketElement];
    var proposedMaximum = void 0;
    switch (maxPercentageMarketElement) {
      case 'target_base_salary':
        proposedMaximum = findMatchingJobFieldValue(job, 'market_base_salary', value);
        break;
      case 'target_total_cash':
        proposedMaximum = findMatchingJobFieldValue(job, 'market_total_cash_paid', value);
        break;
      case 'target_total_target_cash':
        proposedMaximum = findMatchingJobFieldValue(job, 'market_ttc', value);
        break;
      case 'target_total_direct':
        proposedMaximum = findMatchingJobFieldValue(job, 'market_total_direct', value);
        break;
      case 'target_total_guaranteed_pay':
        proposedMaximum = findMatchingJobFieldValue(job, 'total_guaranteed_pay', value);
        break;
      default:
        proposedMaximum = value;
        break;
    }
    if (payType === 'hourly') proposedMaximum = convertAnnualToHourlyValue(proposedMaximum);
    maxValue = proposedMaximum * maxPercentageOfMid;
    if (roundTo !== null) {
      if (rangeType === "market") {
        return Math.round(maxValue / roundTo) * roundTo;
      } else {
        return Math.round(midpoint * maxPercentageOfMid / roundTo) * roundTo;
      }
    } else {
      if (rangeType === "market") {
        return maxValue;
      } else {
        return midpoint * maxPercentageOfMid;
      }
    }
  }
};